import React, { useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import noop from 'lodash/noop';
import { ULABEL_CONFIG } from '../../../../common/constant/constant';
import { useAPIProvider, useAPIProviderGetSignedUrl } from '../../../../common/hooks/use-api-provider';
import MediaErrorComponent from '../../../../common/media-error';
import { type GetULabelConfigFunction } from '../../../../common/providers/feature-flag-provider';
import { mediaFileRefAtom } from '../../../labelling-v2/atoms/file-atoms';
import { multiVideoListAtom, selectedVideoAtom, selectedVideoIndexAtom } from '../../../labelling-v2/atoms/multi-video-atoms';
import { currentFrameAtom } from '../../../labelling-v2/atoms/video-playback-atoms';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { MAIN_VIDEO_ID } from '../../constants';
import { useMediaState } from '../../providers/hooks/use-media-state';
import { useMediaStateLite } from '../../providers/hooks/use-media-state-lite';
import type { MediaItemT } from '../../types';
import { Loader } from '../video/components/loader';
import { Video } from '../video/components/video';
import { MEDIA_MESSAGES } from '../video/constants/error-messages';
import { useMediaLoadingErrorEvents } from '../video/hooks/use-video-loading-error-events';
type PropsT = {
  setFileDimension: (width: number, height: number) => void;
  onVideoLoad: () => void;
  fitToScreen: () => void;
  getULabelConfig?: GetULabelConfigFunction;
};
export function VideoContainer({
  setFileDimension,
  onVideoLoad,
  fitToScreen,
  getULabelConfig
}: PropsT) {
  const {
    isRefreshingSource,
    isAnyLoading,
    mediaLoadingError,
    handleLoading,
    handleError
  } = useMediaLoadingErrorEvents();
  const currentFrame = useAtomValue(currentFrameAtom);
  const {
    onMediaMetadataLoad,
    updateFrameLoadStatus,
    getFrameNumber,
    getFrameTime,
    isPlaying,
    updateToLastFrame,
    playSpeed,
    mediaVolume,
    frameRate,
    onVideoLoadStart,
    decodedFrameRenderer
  } = useMediaState();
  const {
    onMediaMetadataLoad: onMediaMetadataLoadLite,
    isPlaying: isPlayingLite,
    playSpeed: playSpeedLite,
    mediaVolume: mediaVolumeLite,
    currentFrame: currentFrameLite
  } = useMediaStateLite();
  const {
    imageSettingsAtom
  } = useStageAtoms();
  const getSignedUrl = useAPIProviderGetSignedUrl();
  const mediaFileRef = useAtomValue(mediaFileRefAtom);
  const {
    fetchFileSizeAndFormat
  } = useAPIProvider();
  const {
    brightness,
    contrast
  } = useAtomValue(imageSettingsAtom);
  const [multiVideoList, setMultiVideoList] = useAtom(multiVideoListAtom);
  const selectedVideoIndex = useAtomValue(selectedVideoIndexAtom);
  const isMultiVideoIndex = selectedVideoIndex !== 0;
  const commonVideoProps = {
    playbackRate: isMultiVideoIndex ? playSpeedLite : playSpeed,
    volume: isMultiVideoIndex ? mediaVolumeLite : mediaVolume,
    isPlaying: isMultiVideoIndex ? isPlayingLite : isPlaying,
    currentFrame: isMultiVideoIndex ? currentFrameLite : currentFrame
  };
  const signedUrlTTLValue = getULabelConfig?.(ULABEL_CONFIG.SIGNED_URL_TTL);
  const srcExpirationDuration = typeof signedUrlTTLValue === 'number' ? signedUrlTTLValue : null;
  const setCanvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    decodedFrameRenderer?.registerCanvas(canvas);
  }, [decodedFrameRenderer]);
  const selectedVideo = useAtomValue(selectedVideoAtom);
  const mediaUrl = selectedVideo?.url;
  const handleGetSignedUrl = () => {
    if (!getSignedUrl) {
      return Promise.reject(new Error('getSignedUrl is not implemented'));
    }
    return getSignedUrl(selectedVideo?.referenceUri);
  };
  if (!mediaUrl) {
    return null;
  }
  const showLoading = isAnyLoading || decodedFrameRenderer?.isDecoding;
  const showError = !showLoading && !!mediaLoadingError;
  const handleSrcChange = (src: string, mediaItem: MediaItemT) => {
    setMultiVideoList(prev => {
      return prev.map(item => {
        if (item.referenceUri === mediaItem.referenceUri) {
          return {
            ...item,
            url: src
          };
        }
        return item;
      });
    });
  };
  return <>
      <Video key={mediaFileRef} fitToScreen={fitToScreen} id={MAIN_VIDEO_ID} disablePointerEvents={false} brightness={brightness} contrast={contrast} src={mediaUrl} mediaFrameRate={frameRate} videoType="video/mp4" controls={false} preload="auto" {...commonVideoProps} getTimeFromFrameNumber={getFrameTime || null} getFrameNumberFromTime={getFrameNumber || null} onFrameLoadStatus={updateFrameLoadStatus} onLoadedMetadata={(isMultiVideoIndex ? onMediaMetadataLoadLite : onMediaMetadataLoad) || noop} onDimensionChange={setFileDimension} onEndToLastFrame={updateToLastFrame ?? noop} onSrcChange={src => {
      handleSrcChange(src, (multiVideoList[selectedVideoIndex] as MediaItemT));
    }} onLoading={handleLoading} onError={handleError} onLoad={onVideoLoad} onLoadStart={videoUrl => onVideoLoadStart?.(videoUrl, mediaFileRef ?? '')} fetchFileSizeAndFormat={fetchFileSizeAndFormat} getSignedUrl={handleGetSignedUrl} srcExpirationDuration={srcExpirationDuration} />

      <canvas ref={setCanvasRef} style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      opacity: decodedFrameRenderer?.isDecoding ? 0.7 : 1
    }} />

      {showError && <MediaErrorComponent errorMessage={mediaLoadingError?.message} />}

      {showLoading && <Loader message={decodedFrameRenderer?.isDecoding ? MEDIA_MESSAGES.DECODING_FRAME_MESSAGE : isRefreshingSource ? MEDIA_MESSAGES.REFRESHING_URL_MESSAGE : MEDIA_MESSAGES.DEFAULT_LOADING_MESSAGE} />}
    </>;
}